import React from 'react';

import { cn } from '@/lib/utils';

/**
 * Pill variants following Tailwind best practices
 * Using softer, more muted colors per design requirements
 */
export type PillVariant =
  | 'purple' // For IN_PROGRESS, PENDING
  | 'red' // For BLOCKED, IMMEDIATE priority
  | 'green' // For COMPLETED
  | 'gray' // For NOT_APPLICABLE, CLOSED, LOW priority
  | 'orange' // For URGENT priority
  | 'pink' // For PAST DUE priority
  | 'yellow'; // For CHASE

/**
 * Pill size variants
 */
export type PillSize = 'sm' | 'md' | 'lg';

interface PillProps {
  /** Display text for the pill */
  children: React.ReactNode;
  /** Color variant */
  variant: PillVariant;
  /** Size of the pill (default: md) */
  size?: PillSize;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Color scheme matching Figma design (low emphasis style)
 * Uses light backgrounds with dark text for subtle appearance
 */
const PILL_VARIANT_CLASSES: Record<PillVariant, string> = {
  purple:
    'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-200',
  red: 'bg-red-50 text-red-600 dark:bg-red-900/50 dark:text-red-200',
  green:
    'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-200',
  gray: 'bg-zinc-50 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300',
  orange:
    'bg-orange-50 text-orange-600 dark:bg-orange-900/50 dark:text-orange-200',
  pink: 'bg-fuchsia-50 text-fuchsia-600 dark:bg-fuchsia-900/50 dark:text-fuchsia-200',
  yellow:
    'bg-amber-50 text-amber-600 dark:bg-amber-900/50 dark:text-amber-200',
};

const PILL_SIZE_CLASSES: Record<PillSize, string> = {
  sm: 'px-2 py-1 text-[9px] leading-[14px]',
  md: 'px-2.5 py-1 text-[10px] leading-[16px]',
  lg: 'px-3 py-1.5 text-[11px] leading-[18px]',
};

/**
 * Reusable Pill component for badges, status indicators, and labels
 *
 * Features:
 * - Consistent styling across the application
 * - Softer, more muted colors per design requirements
 * - Dark mode support
 * - Multiple size variants
 * - Type-safe variant system
 *
 * Usage:
 * ```tsx
 * <Pill variant="purple">In Progress</Pill>
 * <Pill variant="red" size="sm">Blocked</Pill>
 * <Pill variant="green">Completed</Pill>
 * ```
 */
export const Pill: React.FC<PillProps> = ({
  children,
  variant,
  size = 'md',
  className = '',
}) => {
  const variantClass = PILL_VARIANT_CLASSES[variant];
  const sizeClass = PILL_SIZE_CLASSES[size];

  return (
    <span
      className={cn(
        'inline-flex items-center rounded font-bold uppercase tracking-wider',
        variantClass,
        sizeClass,
        className,
      )}
    >
      {children}
    </span>
  );
};
